import React, { useState, useEffect } from 'react';
import { ArrowLeft, ErrorSharp } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import visa from 'assets/visa.svg';
import amex from 'assets/amex.svg';
import mastercard from 'assets/mastercard.svg';
import unionPay from 'assets/unionpay.svg';
import sepa from 'assets/sepa.png';
import Button from 'components/library/Button/Button';

import Transfer from './Transfer';
import styles from './PayementsForm.module.css';
import { AppDispatch } from 'store/types';
import { IbanTransfer } from 'types/Iban';

const INVESTMENT_BY_CARD_AND_SEPA_LIMIT = 2500;

const PAYMENT_METHODS = {
  CARD: 'card',
  SEPA_DEBIT: 'sepa_debit',
  CUSTOMER_BALANCE: 'customer_balance',
}

interface Props {
  amount: number,
  transfer?: IbanTransfer,
  actions: {
    getPaymentRedirectionLink: (paymentMethod: string) => void;
  }
}

export default function PayementsForm({ amount, transfer, actions }: Props) {
  const [selectedPaymentOption, setSelectedPayementOption] = useState(PAYMENT_METHODS.CARD);
  const [hasSubmit, setHasSubmit] = useState(false);
  const isDebug = window.sessionStorage.getItem('debug') === 'true';

  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams();

  const handleChange = (e: React.MouseEvent<HTMLElement>) => {
    const getName = (element: EventTarget | null): string => {
      if (element instanceof HTMLElement && 'name' in element) {
        return element.name as string;
      }
      return '';
    };
    let name = getName(e.target);
    let currentElement: HTMLElement = e.target as HTMLElement;
    const id = (name) ? name : (
      getName(currentElement.parentElement)
      || getName(currentElement.parentElement && currentElement.parentElement.parentElement));
    if (id === PAYMENT_METHODS.SEPA_DEBIT) {
      dispatch({ type: 'UPDATE_TRANSFER', payload: true });
    }
    if (selectedPaymentOption === PAYMENT_METHODS.SEPA_DEBIT && id !== PAYMENT_METHODS.SEPA_DEBIT) {
      dispatch({ type: 'UPDATE_TRANSFER', payload: false });
    }
    setSelectedPayementOption(id);
  };

  useEffect(() => {
    if (amount) {
      if (amount < INVESTMENT_BY_CARD_AND_SEPA_LIMIT) {
        setSelectedPayementOption(PAYMENT_METHODS.CARD);
      }
      else if (amount >= INVESTMENT_BY_CARD_AND_SEPA_LIMIT) {
        setSelectedPayementOption(PAYMENT_METHODS.CUSTOMER_BALANCE);
      }
      else {
        setSelectedPayementOption(PAYMENT_METHODS.SEPA_DEBIT);
      }
    } else {
      setSelectedPayementOption(PAYMENT_METHODS.SEPA_DEBIT);
    }
  }, [amount]);

  const getPaymentMethod = () => {
    if (amount) {
      if (amount < INVESTMENT_BY_CARD_AND_SEPA_LIMIT) {
        return t('payment.card');
      }
      return t('payment.transfertMethod');
    }
  };

  const handleSubmit = () => {
    actions.getPaymentRedirectionLink(selectedPaymentOption);
    setHasSubmit(true);
    window.setTimeout(() => setHasSubmit(false), 10 * 1000);
  };

  const getBackLink = () => {
    let url = `/${id}`;
    const token = new URL(window.location.href).searchParams.get('token');
    if (token) {
      url = `${url}${url.includes('?') ? '&' : '?'}token=${token}`;
    }
    const email = new URL(window.location.href).searchParams.get('email');
    if (email) {
      url = `${url}${url.includes('?') ? '&' : '?'}email=${email}`;
    }
    return url;
  };

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        {
          (isDebug) && (
            <span className={styles.debug}>
              <ErrorSharp className={styles.icon} />
              Mode debug
            </span>
          )
        }
        {
          (window.matchMedia('(max-width: 425px)').matches)
          && (
            <Link
              to={getBackLink()}
              className={styles.link}
            >
              <ArrowLeft />
              {t('payment.button.return')}
            </Link>
          )
        }
        <h1 className={styles.title}>
          {t('payment.title')}
        </h1>
        <div className={styles.content}>
          <h2 className={styles.subtitle}>
            {t('payment.paymentMethod')}
          </h2>
          <p className={styles.description}>
            {`${t('payment.recommend')} ${getPaymentMethod()} ${t('payment.method')}`}
          </p>
          <button
            type="button"
            name="card"
            className={`${styles.button} ${(selectedPaymentOption === PAYMENT_METHODS.CARD) ? styles.active : ''}`}
            onClick={handleChange}
          >
            <div>
              <input
                type="radio"
                checked={selectedPaymentOption === 'card'}
                readOnly
              />
              <span className={styles.method}>{t('payment.card')}</span>
            </div>
            <div className={styles.card}>
              <img
                src={visa}
                alt="visa"
              />
              <img
                src={amex}
                alt="american express"
              />
              <img
                src={mastercard}
                alt="mastercard"
              />
              <img
                src={unionPay}
                alt="union pay"
              />
            </div>
          </button>
          {/* <button
            type="button"
            className={`${styles.button} ${(selectedPaymentOption === PAYMENT_METHODS.SEPA_DEBIT) ? styles.active : ''} ${(amount < INVESTMENT_BY_CARD_AND_SEPA_LIMIT) ? '' : styles.hide}`}
            name="sepa_debit"
            onClick={handleChange}
          >
            <div>
              <input
                type="radio"
                checked={selectedPaymentOption === PAYMENT_METHODS.SEPA_DEBIT}
                readOnly
              />
              <span className={styles.method}>{t('payment.debiting')}</span>
            </div>
            <img
              src={sepa} alt="Sepa direct debit"
              className={styles.logo}
            />
          </button> */}
          <button
            type="button"
            className={`${styles.button} ${(selectedPaymentOption === PAYMENT_METHODS.CUSTOMER_BALANCE) ? styles.active : ''} ${(amount < INVESTMENT_BY_CARD_AND_SEPA_LIMIT) ? styles.hide : ''}`}
            name="customer_balance"
            onClick={handleChange}
          >
            <div>
              <input
                type="radio"
                checked={selectedPaymentOption === PAYMENT_METHODS.CUSTOMER_BALANCE}
                readOnly
              />
              <span className={styles.method}>{t('payment.transfert.title')}</span>
            </div>
          </button>
          <h2 className={styles.subtitle}>
            {t('payment.information')}
          </h2>
          {
            (transfer && (selectedPaymentOption === PAYMENT_METHODS.CUSTOMER_BALANCE))
              ? (
                <Transfer
                  transfer={transfer}
                />
              )
              : (
                <p className={styles.description}>
                  {t('payment.stripe')}
                </p>
              )
          }
          {
            (isDebug)
              ? (
                <p className={styles.debugSubmit}>
                  <Link
                    to={`/${id}/confirmation`}
                  >
                    Passer à la confirmation (DEBUG)
                  </Link>
                </p>
              )
              : (
                !(transfer && (selectedPaymentOption === PAYMENT_METHODS.CUSTOMER_BALANCE))
                && (<Button
                  label={t('payment.button.payment')}
                  className={styles.submit}
                  onButtonClick={handleSubmit}
                  disabled={hasSubmit}
                />)
              )
          }
        </div>
        <Link
          to={getBackLink()}
          className={styles.link}
        >
          <ArrowLeft />
          {t('payment.button.return')}
        </Link>
      </div>
    </div>
  )
}
